import { useCallback, useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../shared/theme/paletteDark';
import { usePhoneChrome } from '../shared/usePhoneChrome';
import { toggleBandPx } from './viewToggleMetrics';

/**
 * A sheet half up over the day view: the event callout below 900px, and the
 * personal event form on a phone. A phone's callout over anything else is one
 * too, at full height (`fullOnly`).
 *
 * It opens only this far, and the day scrolls what it is about into the middle
 * of what is left above it, so a change made in the sheet — "I arrive", a new
 * event's hours — can be watched landing in the day. Swiping up (or scrolling
 * up, with a wheel) takes it to full height, where its contents scroll as
 * usual. Pulling it down from full height is judged by how: a quick flick
 * closes it, and a slower drag settles wherever the release is nearest — back
 * to full, down to half, or closed. From half height, down closes it. The day
 * above stays live and scrollable.
 *
 * `holdAtHalf` is for something that would be lost by closing, like a form with
 * input in it: then no gesture closes the sheet, and the furthest one goes is
 * half height. Its own buttons still close it.
 *
 * `fullOnly` is the phone's callout over a list rather than the day: it opens
 * at full height and has no half, so down from full closes it. With `scrim`
 * the page behind is dimmed and a tap on it closes the sheet.
 *
 * The contents mark their scrolling box `data-callout-scroll`, and bars that
 * always drag the sheet `data-callout-chrome`. The sheet is laid out at its
 * full height and slid down, so half height is simply the share of it left
 * showing: `peekTop`'s, or with `measured` whatever the contents marked
 * `data-half-anchor` need. A measured sheet half up writes where its top edge
 * rests, in px from the top of the screen, as `data-half-top`, for the day to
 * centre a card above it. It moves by transform, written straight to the
 * element.
 */

/**
 * Share of the screen above the form while it is half up: a form half up is a
 * thing you are writing in, and its fields have to fit.
 */
export const PEEK_TOP = 0.39;
/**
 * The callout half up, with `measured`: tall enough to show everything marked
 * `data-half-anchor` (its header, title and the phone's arrive and leave
 * rows), never less than a third of the screen and never more than 45%.
 *
 * Half up, the callout is a glance at one event *while looking at the day*, so
 * the day above keeps most of the screen. A fixed third cut off the arrive and
 * leave rows under a long title, and they are the reason to look (decision 3).
 */
export const HALF_MIN = 1 / 3;
export const HALF_MAX = 0.45;
/** Room under the last anchor, so the rows don't end on the screen's edge. */
const ANCHOR_PAD = 14;
/** Its full height, as a share of the screen: the phone callout's usual height. */
const FULL = 0.88;

const RISE_MS = 380;
const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';
/** Movement below this is still a tap. */
const SLOP = 6;
/** px/ms. A pull down from full height this fast closes the callout outright. */
const FLICK_DOWN = 0.5;

const Frame = styled('div')({
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1200,
  height: `${FULL * 100}vh`,
  display: 'flex',
  flexDirection: 'column',
  background: BRAND.mocha,
  borderTop: '1px solid rgba(243,238,231,0.35)',
  // Drawn only while the sheet is up. Parked below the screen it still bled
  // about 30px of blur up over the bottom edge of the page, where two parked
  // sheets darkened the ground under the toggle.
  boxShadow: 'none',
  transform: 'translateY(100%)',
  willChange: 'transform',
});

/** The page behind, dimmed as MUI's modals dim it. */
const Scrim = styled('div')({
  position: 'fixed',
  inset: 0,
  zIndex: 1199,
  background: 'rgba(0,0,0,0.5)',
  opacity: 0,
  pointerEvents: 'none',
  transition: 'opacity 280ms ease',
});

type Snap = 'half' | 'full';

interface Props {
  open: boolean;
  /** What is shown. Something new while open goes back to where it opened. */
  contentKey: string | null;
  onClose: () => void;
  /** Finished sliding away: the contents can go. */
  onClosed: () => void;
  /** No gesture closes it: half height is as far down as a swipe goes. */
  holdAtHalf?: boolean;
  /** How much of the screen stays above it at half height. */
  peekTop?: number;
  /** Half height fits the contents' `data-half-anchor` parts instead. */
  measured?: boolean;
  /** Opens at full height and has no half: down from full closes it. */
  fullOnly?: boolean;
  /** Dims the page behind, and a tap on it closes the sheet. */
  scrim?: boolean;
  children: ReactNode;
}

export default function HalfSheet({
  open,
  contentKey,
  onClose,
  onClosed,
  holdAtHalf = false,
  peekTop = PEEK_TOP,
  measured = false,
  fullOnly = false,
  scrim = false,
  children,
}: Props) {
  const phoneChrome = usePhoneChrome();
  const frame = useRef<HTMLDivElement | null>(null);
  const shade = useRef<HTMLDivElement | null>(null);
  const opensAt: Snap = fullOnly ? 'full' : 'half';
  const snap = useRef<Snap>(opensAt);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ onClose, onClosed, open, holdAtHalf });
  latest.current = { onClose, onClosed, open, holdAtHalf };

  const scrollBox = () => frame.current?.querySelector<HTMLElement>('[data-callout-scroll]') ?? null;

  /** How far down the sheet its last `data-half-anchor` part ends, however its contents are scrolled. */
  const anchorsEnd = useCallback(() => {
    const el = frame.current;
    if (!el) return 0;
    const top = el.getBoundingClientRect().top;
    const scrolled = scrollBox()?.scrollTop ?? 0;
    let end = 0;
    for (const part of el.querySelectorAll<HTMLElement>('[data-half-anchor]')) {
      // A held header is where it is; everything else has scrolled up by `scrolled`.
      const held = getComputedStyle(part).position === 'sticky';
      end = Math.max(end, part.getBoundingClientRect().bottom - top + (held ? 0 : scrolled));
    }
    // A phone's toggle sits over the sheet's bottom edge: the last anchor row
    // has to end above its band, not 14px above the screen.
    return end + ANCHOR_PAD + (phoneChrome ? toggleBandPx() : 0);
  }, [phoneChrome]);

  /** Where the sheet sits, in px down from fully up. */
  const yOf = useCallback((state: Snap | 'closed') => {
    const height = frame.current?.offsetHeight ?? window.innerHeight * FULL;
    const screen = window.innerHeight;
    if (state === 'full') return 0;
    if (state === 'half') {
      const showing = measured
        ? Math.min(Math.max(screen * HALF_MIN, anchorsEnd()), screen * HALF_MAX)
        : screen * (1 - peekTop);
      return Math.max(0, height - showing);
    }
    return height + 12;
  }, [peekTop, measured, anchorsEnd]);

  const dim = useCallback((on: boolean) => {
    const el = shade.current;
    if (!el) return;
    el.style.opacity = on ? '1' : '0';
    el.style.pointerEvents = on ? 'auto' : 'none';
  }, []);

  const paint = useCallback((y: number, ms = 0) => {
    const el = frame.current;
    if (!el) return;
    el.style.transition = ms ? `transform ${ms}ms ${EASE_OUT}` : 'none';
    el.style.transform = `translateY(${y}px)`;
  }, []);

  /** The shadow belongs to a sheet that is up; a parked one has none. */
  const shadow = useCallback((on: boolean) => {
    const el = frame.current;
    if (el) el.style.boxShadow = on ? '0 -12px 32px rgba(0,0,0,0.35)' : '';
  }, []);

  /**
   * Rest at half or full. At half the contents don't scroll, so a swipe moves
   * the sheet, and they go back to the top, where the event's hours are.
   */
  const rest = useCallback(
    (state: Snap, ms = RISE_MS) => {
      snap.current = state;
      const box = scrollBox();
      if (box) {
        if (state === 'half' && box.scrollTop > 0) box.scrollTo({ top: 0, behavior: 'smooth' });
        box.style.overflowY = state === 'half' ? 'hidden' : '';
      }
      const y = yOf(state);
      const el = frame.current;
      if (el && measured) {
        if (state === 'half') el.dataset.halfTop = String(Math.round(window.innerHeight - el.offsetHeight + y));
        else delete el.dataset.halfTop;
      }
      paint(y, ms);
    },
    [paint, yOf, measured],
  );

  /** Slide away, then say so. */
  const dismiss = useCallback(
    (ms = 280) => {
      // Holding: a gesture that would close settles at half instead.
      if (latest.current.holdAtHalf && latest.current.open) {
        rest('half', 300);
        return;
      }
      dim(false);
      if (frame.current) delete frame.current.dataset.halfTop;
      paint(yOf('closed'), ms);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        shadow(false);
        if (latest.current.open) latest.current.onClose();
        latest.current.onClosed();
      }, ms);
    },
    [paint, rest, yOf, dim, shadow],
  );

  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Opened, or a different event: rise to where it opens, from below if it was closed.
  useLayoutEffect(() => {
    if (!open) return;
    window.clearTimeout(timer.current);
    const box = scrollBox();
    if (box) box.scrollTop = 0;
    const el = frame.current;
    if (el && el.style.transform === '') {
      paint(yOf('closed'));
      el.getBoundingClientRect();
    }
    dim(true);
    shadow(true);
    rest(opensAt);
  }, [open, contentKey, opensAt, paint, rest, yOf, dim, shadow]);

  // Half up and measured, it follows its anchors: the arrive and leave rows
  // appearing, "Attend it all" under them, a title reflowing as a font loads.
  useEffect(() => {
    const content = scrollBox()?.firstElementChild;
    if (!open || !measured || !content) return;
    let last = yOf('half');
    const watch = new ResizeObserver(() => {
      const y = yOf('half');
      if (snap.current === 'half' && Math.abs(y - last) > 1) rest('half', 200);
      last = y;
    });
    watch.observe(content);
    return () => watch.disconnect();
  }, [open, contentKey, measured, rest, yOf]);

  // Closed from inside (the ✕, an action that closes it): slide away.
  useLayoutEffect(() => {
    if (open) return;
    const el = frame.current;
    if (!el || el.style.transform === '') return;
    dismiss();
  }, [open, dismiss]);

  // Keep the resting place right when the screen changes size.
  useEffect(() => {
    const onResize = () => {
      if (latest.current.open) rest(snap.current, 0);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [rest]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  /**
   * Where a vertical drag lands. Up from half goes full, and down from half
   * closes. Down from full, a quick flick closes it; a slower drag settles at
   * whichever of full, half and closed the release is nearest.
   */
  const settle = useCallback(
    (from: Snap, dy: number, velocity: number) => {
      if (from === 'half') {
        if (dy < -40 || velocity < -0.4) rest('full', 300);
        else if (dy > 60 || velocity > 0.4) dismiss();
        else rest('half', 240);
        return;
      }
      if (velocity > FLICK_DOWN && dy > 30) {
        dismiss();
        return;
      }
      const released = Math.max(0, dy);
      const stops: [Snap | 'closed', number][] = [
        ['full', 0],
        ...(fullOnly ? [] : [['half', yOf('half')] as [Snap, number]]),
        ['closed', yOf('closed')],
      ];
      const [nearest] = stops.reduce((best, stop) =>
        Math.abs(released - stop[1]) < Math.abs(released - best[1]) ? stop : best,
      );
      if (nearest === 'closed') dismiss();
      else rest(nearest, 320);
    },
    [dismiss, rest, yOf, fullOnly],
  );

  // Touch: listeners of our own, because a pull down at the top of the contents
  // has to stop the browser scrolling, and only a non-passive touchmove can.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;

    let press: {
      x: number;
      y: number;
      from: Snap;
      base: number;
      chrome: boolean;
      atTop: boolean;
      mode: 'pending' | 'sheet' | 'native';
      lastY: number;
      lastT: number;
      velocity: number;
    } | null = null;

    const onStart = (e: TouchEvent) => {
      const target = e.target as Element | null;
      if (e.touches.length > 1 || target?.closest('[data-own-swipe]')) {
        press = null;
        return;
      }
      const t = e.touches[0];
      press = {
        x: t.clientX,
        y: t.clientY,
        from: snap.current,
        base: yOf(snap.current),
        chrome: Boolean(target?.closest('[data-callout-chrome]')),
        atTop: (scrollBox()?.scrollTop ?? 0) <= 0,
        mode: 'pending',
        lastY: t.clientY,
        lastT: e.timeStamp,
        velocity: 0,
      };
    };

    const onMove = (e: TouchEvent) => {
      if (!press || e.touches.length > 1) return;
      const t = e.touches[0];
      const dx = t.clientX - press.x;
      const dy = t.clientY - press.y;

      if (press.mode === 'pending') {
        const pullingDown = dy > 0 && press.atTop && (scrollBox()?.scrollTop ?? 0) <= 0;
        // Stop the browser before it starts a scroll we mean to take over.
        if (press.from === 'half' || press.chrome || pullingDown) e.preventDefault();
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        if (Math.abs(dx) > Math.abs(dy)) press.mode = 'native';
        else if (press.from === 'half' || press.chrome || pullingDown) press.mode = 'sheet';
        else press.mode = 'native';
      }
      if (press.mode !== 'sheet') return;

      e.preventDefault();
      const dt = e.timeStamp - press.lastT;
      if (dt > 0) press.velocity = (t.clientY - press.lastY) / dt;
      press.lastY = t.clientY;
      press.lastT = e.timeStamp;
      // Above full height it resists: there is nothing higher to go.
      const y = press.base + dy;
      paint(y < 0 ? y * 0.2 : y);
    };

    const onEnd = (e: TouchEvent) => {
      if (!press) return;
      const { mode, from, y, lastT, velocity } = press;
      const lastY = press.lastY;
      press = null;
      if (mode !== 'sheet') return;
      const flick = e.timeStamp - lastT < 90 ? velocity : 0;
      settle(from, lastY - y, flick);
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [paint, settle, yOf]);

  // A mouse: the same drags, from anything that isn't a control.
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const target = e.target as Element;
    if (target.closest('button, a, input, textarea, select, [data-own-swipe]')) return;
    const from = snap.current;
    const base = yOf(from);
    const chrome = Boolean(target.closest('[data-callout-chrome]'));
    const startY = e.clientY;
    let moving = false;
    let lastY = startY;
    let lastT = e.timeStamp;
    let velocity = 0;

    const move = (ev: PointerEvent) => {
      const dy = ev.clientY - startY;
      if (!moving) {
        if (Math.abs(dy) < SLOP) return;
        const atTop = (scrollBox()?.scrollTop ?? 0) <= 0;
        if (from === 'full' && !chrome && !(dy > 0 && atTop)) return stop();
        moving = true;
      }
      const dt = ev.timeStamp - lastT;
      if (dt > 0) velocity = (ev.clientY - lastY) / dt;
      lastY = ev.clientY;
      lastT = ev.timeStamp;
      const y = base + dy;
      paint(y < 0 ? y * 0.2 : y);
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    const up = (ev: PointerEvent) => {
      stop();
      if (moving) settle(from, ev.clientY - startY, ev.timeStamp - lastT < 90 ? velocity : 0);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // A wheel: down through the contents from half height opens it fully. Up at
  // the top of the contents steps down, full to half and half to closed, one
  // step per scroll: only a scroll that began at the top, never the tail of one
  // that just carried the contents back there, and never the rest of the scroll
  // that took the last step.
  const wheel = useRef({ sum: 0, last: 0, startedAtTop: false, spent: false });
  const onWheel = useCallback(
    (e: WheelEvent) => {
      const w = wheel.current;
      const box = scrollBox();
      const atTop = (box?.scrollTop ?? 0) <= 0;
      if (e.timeStamp - w.last > 250) {
        w.sum = 0;
        w.startedAtTop = atTop;
        w.spent = false;
      }
      // A scroll that has been anywhere but the top is reading, not closing.
      if (!atTop) w.startedAtTop = false;
      w.last = e.timeStamp;
      if (w.spent) {
        e.preventDefault();
        return;
      }
      if (snap.current === 'half') {
        e.preventDefault();
        w.sum += e.deltaY;
        if (w.sum > 30) {
          w.spent = true;
          rest('full', 300);
        } else if (w.sum < -60) {
          w.spent = true;
          dismiss();
        }
        return;
      }
      if (e.deltaY < 0 && atTop && w.startedAtTop) {
        e.preventDefault();
        w.sum += e.deltaY;
        if (w.sum < -80) {
          w.spent = true;
          if (fullOnly) dismiss();
          else rest('half', 320);
        }
      }
    },
    [dismiss, rest, fullOnly],
  );

  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  return (
    <>
      {scrim && <Scrim ref={shade} onClick={() => dismiss()} aria-hidden />}
      <Frame ref={frame} onPointerDown={onPointerDown} role="presentation">
        {children}
      </Frame>
    </>
  );
}
